import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Layout } from "./components/Layout";
import { CenteredSpinner, Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { ForcePasswordChange } from "./pages/ForcePasswordChange";
import { Dashboard } from "./pages/Dashboard";
import { Projects } from "./pages/Projects";
import { ProjectDetail } from "./pages/ProjectDetail";
import { MyTasks } from "./pages/MyTasks";
import { NotificationsPage } from "./pages/NotificationsPage";
import { ApprovalsPage } from "./pages/ApprovalsPage";
import { Profile } from "./pages/Profile";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminRoles } from "./pages/admin/AdminRoles";
import { AdminLookups } from "./pages/admin/AdminLookups";
import { AdminCustomFields } from "./pages/admin/AdminCustomFields";
import { AdminActivityLog } from "./pages/admin/AdminActivityLog";
import { AdminSettings } from "./pages/admin/AdminSettings";

function RequirePerm({ perm, children }: { perm: string; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function LandingRedirect() {
  const { can } = useAuth();
  return <Navigate to={can("dashboard.portfolio") ? "/dashboard" : "/my-tasks"} replace />;
}

export function App() {
  const { user, loading } = useAuth();
  const loc = useLocation();

  if (loading) return <CenteredSpinner />;

  // Public routes
  if (!user) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  if (user.mustChangePassword && loc.pathname !== "/reset-password") {
    return <ForcePasswordChange />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Layout />}>
        <Route index element={<LandingRedirect />} />
        <Route path="/dashboard" element={<RequirePerm perm="dashboard.portfolio"><Dashboard /></RequirePerm>} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id/*" element={<ProjectDetail />} />
        <Route path="/my-tasks" element={<MyTasks />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin/users" element={<RequirePerm perm="users.manage"><AdminUsers /></RequirePerm>} />
        <Route path="/admin/roles" element={<RequirePerm perm="roles.manage"><AdminRoles /></RequirePerm>} />
        <Route path="/admin/lookups" element={<RequirePerm perm="lookups.manage"><AdminLookups /></RequirePerm>} />
        <Route path="/admin/custom-fields" element={<RequirePerm perm="custom_fields.manage"><AdminCustomFields /></RequirePerm>} />
        <Route path="/admin/activity-log" element={<RequirePerm perm="activity_log.view"><AdminActivityLog /></RequirePerm>} />
        <Route path="/admin/settings" element={<RequirePerm perm="platform.manage"><AdminSettings /></RequirePerm>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
